import * as React from "react"
import { Modal, Flag, FlagNameValues, Button } from "semantic-ui-react"
import FormField, { IFormFieldProps, IMultilangProps } from "."
import { FieldType, LockStatus } from "../../../models"
import {
  ILocalizedString,
  Multilang,
  MultilangString,
} from "../../../models/product/multilang-string"
import { getlangFromCache, Languages, languages } from "../../../utils"
import {
  FormFieldTypes,
  IFieldReducers,
  IFieldState,
} from "../../product-form/_reducers/_utils/product-form-types"
import { StateFromReducersMapObject } from "../../../utils/combine-reducer"

export interface IMultilangFieldState {
  open: boolean
  keyPressed: string[]
}

interface IHandeChangeMultilangProps {
  code: string
  fieldValue: string
  lock: LockStatus
  fieldType: FieldType
  lang: Languages
  overrideDefaultValue: boolean
}

interface IOnChangeProps {
  code: string
  fieldValue: string
  lock: "lock" | "unlock"
  fieldType: FieldType
  lang?: string
  overrideDefaultValue: boolean
}

const MultilangField = (props: IFormFieldProps<MultilangString>) => {
  const [open, setOpen] = React.useState(false)
  const [keyPressed, setKeyPressed] = React.useState<string[]>([])

  const handleChange = ({
    code,
    fieldValue,
    lock,
    fieldType,
    lang,
    overrideDefaultValue,
  }: IHandeChangeMultilangProps) => {
    // console.log("OnChange")
    const newValue = Multilang.String.SetValue(
      props.data.value,
      lang,
      fieldValue
    )
    props.onChange({
      code,
      fieldValue: newValue,
      lock,
      fieldType,
      overrideDefaultValue,
    })
  }

  const handleKeyDown = (e: any) => {
    const { multiLine = true } = props.multilangProps || {
      multiLine: true,
    }

    if (
      e.keyCode === 13 &&
      ((props.data.formFieldType === FormFieldTypes.Textarea &&
        multiLine === false) ||
        !(props.data.formFieldType === FormFieldTypes.Textarea))
    ) {
      e.stopPropagation()
      e.preventDefault()
      return handleClose()
    }

    if (
      (e.keyCode === 13 || e.keyCode === 17) &&
      props.data.formFieldType === FormFieldTypes.Textarea
    ) {
      const k = [...keyPressed, e.keyCode]

      if (k.some((el) => el === 13) && k.some((el) => el === 17)) {
        e.stopPropagation()
        e.preventDefault()
        return handleClose()
      }

      setKeyPressed(k)
    }
  }

  const handleKeyUp = (e: any) => {
    if (
      (e.keyCode === 13 || e.keyCode === 17) &&
      props.data.formFieldType === FormFieldTypes.Textarea
    ) {
      setKeyPressed(keyPressed.filter((keyCode) => keyCode !== e.keyCode))
    }
  }

  const onChangeTrigger = ({
    code,
    fieldValue,
    lock,
    fieldType,
    overrideDefaultValue,
  }: IOnChangeProps) => {
    const lang = props.lang
    const newValue = Multilang.String.SetValue(
      props.data.value,
      lang || Languages.FR,
      fieldValue
    )
    props.onChange({
      code,
      fieldValue: newValue,
      lock,
      fieldType,
      overrideDefaultValue,
    })
    handleOpen()
  }

  const handleOpen = () => {
    setOpen(true)
    setKeyPressed([])
  }

  const handleClose = () => {
    setOpen(false)
    const tabbables = Array.from(
      document.querySelectorAll<HTMLElement>(".tabbable")
    )
    tabbables.forEach((element: HTMLElement) => {
      if (element.tabIndex === (props.tabIndex || 1) + 1) {
        element.focus()
      }
    })
  }

  const handleLooseFocus = () => {
    props.onFieldLooseFocus(props.data)
  }

  const { data, multilangProps, lang, oldValue } = props
  const langs = languages.sort((a: string, b: string) => (a === lang ? -1 : +1))
  const { value, code, label } = data

  const { size = "mini" } = multilangProps || {
    size: "mini",
  }

  let index = 500

  return (
    <Modal
      trigger={
        <Trigger
          originalProps={props}
          handleLooseFocus={handleLooseFocus}
          handleOpen={handleOpen}
          onChangeTrigger={onChangeTrigger}
        />
      }
      className="multilang-modal"
      size={size}
      open={open}
      dimmer="blurring"
      key={code}
      onClose={handleClose}
    >
      <Modal.Header>{props.label}</Modal.Header>
      <Modal.Content className="multilang-modal-content">
        {langs.map((l: Languages) => {
          return (
            <FormField
              {...props}
              data={getSubData(data, l, false)}
              oldValue={
                oldValue && {
                  ...oldValue,
                  value: oldValue.isEmpty
                    ? oldValue.value
                    : Multilang.String.GetValue(
                        oldValue.value as ILocalizedString[],
                        l,
                        false
                      ),
                }
              }
              lang={l}
              tabIndex={index++}
              key={code + l}
              label={<Flag name={l === "en" ? "gb" : l} />}
              // @ts-ignore
              onChange={handleChange}
              onFieldLooseFocus={handleLooseFocus}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
            />
          )
        })}
      </Modal.Content>
      <Modal.Actions>
        <Button
          positive
          inverted
          icon="checkmark"
          labelPosition="right"
          content="Ok"
          onClick={handleClose}
        />
      </Modal.Actions>
    </Modal>
  )
}

interface ITriggerProps {
  originalProps: IFormFieldProps<MultilangString>
  onChangeTrigger: (payload: IOnChangeProps) => void
  handleOpen: () => void
  handleLooseFocus: () => void
}
const Trigger = ({
  originalProps,
  onChangeTrigger,
  handleOpen,
  handleLooseFocus,
}: ITriggerProps) => {
  const subData = React.useMemo(
    () => getSubData(originalProps.data, originalProps.lang),
    [originalProps.data, originalProps.lang]
  )
  return (
    <FormField
      {...originalProps}
      data={subData}
      oldValue={
        originalProps.oldValue && {
          ...originalProps.oldValue,
          value: originalProps.oldValue.isEmpty
            ? originalProps.oldValue.value
            : Multilang.String.GetValue(
                originalProps.oldValue.value as ILocalizedString[],
                originalProps.lang || getlangFromCache(),
                true
              ),
        }
      }
      // @ts-ignore
      onChange={onChangeTrigger}
      onLockClick={originalProps.onLockClick}
      onclick={handleOpen}
      onFieldLooseFocus={handleLooseFocus}
      // onfocus={this.handleOpen}
    />
  )
}

const getSubData = (
  data: IFieldState<MultilangString>,
  l?: Languages,
  isPreviewText = true
): IFieldState<string> => {
  return {
    ...data,
    value: Multilang.String.GetValue(
      data.value,
      l || getlangFromCache(),
      isPreviewText
    ),
    localized: false,
    errorText: isPreviewText ? data.errorText : "",
    regenerateInfos: { ...data.regenerateInfos, dataType: "" },
  }
}

export default MultilangField
